import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { getBalanceAndPin } from "../../features/balance-and-pin/balanceAndPinSlice";
import { getDasDeviceDetails } from "../../features/das-device-manager/dasDeviceManagerSlice";
import { getWeatherDataAction } from "../../features/weather-widget/weatherWidgetSlice";

import Program from "./Program";
import Balance from "./Balance";
import YourStay from "./YourStay";
import Memories from "./Memories";
import WeatherWidget from "./WeatherWidget";
import Spinner from "../../components/Spinner";

import {
  MainContainer,
  NavBar,
  IntroContainer,
  DiscoverContainer,
  RacesContainer,
  GetToKnowContainer,
  LanguageGuideContainer,
  FooterContainer,
  Separator,
  ScheduleInfoContainer,
  DiscoverItem
} from "./styles";

import logo from "./imgs/planica-logo.svg";
import yourStay from "./imgs/your-stay.png";
import program from "./imgs/program.png";
import balance from "./imgs/balance.webp";
import memories from "./imgs/memories.webp";
import getToKnow from "./imgs/get-to-know.png";
import localAttractions from "./imgs/local-attractions.png";
import poweredBy from "./imgs/digiseq-logo.svg";
import copyIconWhite from "./imgs/copy-icon-white.svg";
import paymeswissLogo from "./imgs/paymeswiss_logo.svg";

import packageJson from "../../../package.json";
import { usePromoTracking } from "../../hooks/usePromoTracking";

const parseBold = (text) =>
  text.split(/(<b>.*?<\/b>)/g).map((part, i) => {
    const m = part.match(/^<b>(.*?)<\/b>$/);
    return m ? (
      <span key={i} className="bold">
        {m[1]}
      </span>
    ) : (
      part
    );
  });

const Planica26 = () => {
  const [searchParams] = useSearchParams();
  const serialNumber = searchParams.get("serialNumber");
  const eventId = searchParams.get("eventId");

  const [copied, setCopied] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const dispatch = useDispatch();
  const { balanceAndPin, isLoading } = useSelector(
    (state) => state.balanceAndPin
  );
  const { deviceDetails, gettingDeviceDetails, errorGettingDeviceDetails } =
    useSelector((state) => state.dasDevices);

  const { weatherData, loadingWeather, errorWeather } = useSelector(
    (state) => state.weatherWidget
  );
  const trackingEventId = deviceDetails?.attendance?.eventId || eventId;
  const trackingGuestId = deviceDetails?.attendance?.guestId;
  const { trackButtonClick, trackUserAction } = usePromoTracking({
    eventId: trackingEventId,
    guestId: trackingGuestId,
    serialNumber,
    pageTitle: "Planica 2026"
  });
  const hasTrackedPageEnd = useRef(false);
  const latestTrackUserAction = useRef(() => {});

  const trackPlanicaButtonClick = useCallback(
    (buttonName, location) => {
      trackButtonClick({
        buttonName,
        location
      });
    },
    [trackButtonClick]
  );

  const trackPlanicaUserAction = useCallback(
    (userAction, location = "planica26_page") => {
      trackUserAction({
        userAction,
        location
      });
    },
    [trackUserAction]
  );

  const getHotelInfo = () => {
    const group = deviceDetails?.group;
    const customFields = group?.customFields;
    const hotelName = customFields?.hotelName || group?.name;

    if (!hotelName) {
      return null;
    }

    return {
      options: [
        {
          id: "hotel",
          item: hotelName,
          additionalData: {
            image: customFields?.image || "",
            address: customFields?.address || ""
          }
        }
      ]
    };
  };

  const getWalletURL = () => {
    return deviceDetails?.group?.customFields?.walletURL || null;
  };

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    dispatch(getWeatherDataAction({ latitude: 46.487, longitude: 13.736 }));
  }, [dispatch]);

  useEffect(() => {
    if (isMounted) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied, isMounted]);

  useEffect(() => {
    if (!balanceAndPin && serialNumber) {
      dispatch(
        getBalanceAndPin({
          serial: serialNumber,
          query: "?provider=pannovate"
        })
      );
    }
  }, [balanceAndPin, serialNumber, dispatch]);

  useEffect(() => {
    if (serialNumber && eventId) {
      dispatch(
        getDasDeviceDetails({
          serial: serialNumber,
          eventId,
          locale: "en"
        })
      );
    }
  }, [serialNumber, eventId, dispatch]);

  useEffect(() => {
    latestTrackUserAction.current = trackPlanicaUserAction;
  }, [trackPlanicaUserAction]);

  const [isMemoriesOpen, setIsMemoriesOpen] = useState(false);
  const [isBalanceOpen, setIsBalanceOpen] = useState(false);
  const [isProgramOpen, setIsProgramOpen] = useState(false);
  const [isYourStayOpen, setIsYourStayOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (hasTrackedPageEnd.current) {
        return;
      }

      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 40
      ) {
        hasTrackedPageEnd.current = true;
        latestTrackUserAction.current("page_reached_end");
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        latestTrackUserAction.current("page_hidden");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      latestTrackUserAction.current("page_navigated_away");
    };
  }, []);

  const handleClose = (closeItem) => {
    trackPlanicaButtonClick(`close_${closeItem}`, `${closeItem}_modal`);
    const discoverContainer = document.getElementById("discover");
    discoverContainer.scrollIntoView({ behavior: "smooth" });

    switch (closeItem) {
      case "memories":
        setIsMemoriesOpen(false);
        break;
      case "balance":
        setIsBalanceOpen(false);
        break;
      case "program":
        setIsProgramOpen(false);
        break;
      case "yourStay":
        setIsYourStayOpen(false);
        break;
      default:
        break;
    }
  };

  const handleOpen = (openItem) => {
    trackPlanicaButtonClick(`open_${openItem}`, "discover_section");
    window.scrollTo(0, 0);
    switch (openItem) {
      case "memories":
        setIsMemoriesOpen(true);
        break;
      case "balance":
        setIsBalanceOpen(true);
        break;
      case "program":
        setIsProgramOpen(true);
        break;
      case "yourStay":
        setIsYourStayOpen(true);
        break;
      default:
        break;
    }
  };

  if (isLoading || gettingDeviceDetails) return <Spinner />;

  return (
    <MainContainer>
      <NavBar>
        <img src={logo} alt="logo" />
      </NavBar>
      <IntroContainer>
        <div className="image-container">
          {deviceDetails?.attendance?.guestFirstName && (
            <h1>
              {"Welcome,"}{" "}
              <span className="new-line">
                {deviceDetails?.attendance?.guestFirstName}{" "}
                {deviceDetails?.attendance?.guestLastName}
              </span>
            </h1>
          )}
        </div>

        <div className="text-container">
          <h2>{"Zdravo!"}</h2>
          <p>
            {
              "We are excited you will be taking part in the FIS Ski Jumping World Cup Finals in Planica this year. Enjoy a premium experience, priceless surprises, and long flights from the world's best ski flyers."
            }
          </p>
        </div>
      </IntroContainer>

      <ScheduleInfoContainer>
        <p>
          {
            "The Mastercard Hospitality Team will be available throughout the event and positioned in the hotel lobby 30 minutes prior to all listed meeting times. Please adhere to timings as transportation will depart promptly."
          }
        </p>
        <p>
          {
            "For your personalised daily schedule, open the Program section below."
          }
        </p>
      </ScheduleInfoContainer>
      <Separator isLeft={true} />
      <DiscoverContainer id="discover">
        <h2>{"Experience Planica Valley"}</h2>
        <p>{"Tap to find out what there is to discover"}</p>
        <div className="items-container">
          <DiscoverItem onClick={() => handleOpen("yourStay")}>
            <img src={yourStay} alt="your stay" />
          </DiscoverItem>
          <DiscoverItem onClick={() => handleOpen("program")}>
            <img src={program} alt="program" />
          </DiscoverItem>
          <DiscoverItem onClick={() => handleOpen("balance")}>
            <img src={balance} alt="balance" />
          </DiscoverItem>
          <DiscoverItem onClick={() => handleOpen("memories")}>
            <img src={memories} alt="memories" />
          </DiscoverItem>
        </div>
      </DiscoverContainer>
      <Separator />
      <RacesContainer>
        <h2>{"FIS Ski Jumping World Cup Finals Planica 2026"}</h2>
        <p>{"Peak of Ski Jumping"}</p>
        <p>
          {parseBold(
            "The annual <b>FIS Ski Jumping World Cup Finals in Planica</b> is the biggest social and sporting event of the season that attracts more than 100.000 live visitors. It is broadcasted to millions of fans all over the world. And this year, for the first time in history, women will take flight on the famous Gorišek Brothers Flying Hill."
          )}
        </p>
        <p>
          {
            "Over the course of four days, Planica will host four competitions: two men’s individual events, one women’s individual event, and one men’s team event. With this, Planica will take a historic step forward and reaffirm its statues as Slovenia’s biggest sporting event, a title it has proudly carried for decades as “the greatest Slovenian sports festival”."
          }
        </p>
        <p>
          {parseBold(
            "<b>The four main events</b>, including Men's Qualifications, Men's Individual Competition & Women's Training, Men's Team Competition & Women's Individual Competition and Men's Individual Competition will take place in Planica, Slovenia on March 26-29, 2026, and will be attended by fans from all over the world."
          )}
        </p>
      </RacesContainer>
      <GetToKnowContainer>
        <img src={getToKnow} alt="get to know planica" />
        <div className="text-container">
          <h2>{"Get to know Planica"}</h2>
        </div>
        <Separator />
        <div className="text-container">
          <p>
            {parseBold(
              "<b>Planica</b> is located in the <b>heart of Europe</b> and in the <b>Northwest Slovenia</b>. It is easy to reach from anywhere in the world, due to its proximity to the main Slovenian airport and many other airports in the neighboring countries."
            )}
          </p>
          <p>
            {parseBold(
              "Nestled in a beautiful <b>Alpine valley</b>, Planica is one of the world’s most iconic ski jumping destinations. Known for its legendary flying hill and record-breaking jumps, it combines <b>rich sporting tradition with stunning nature</b>. Visitors can explore the <b>Nordic Centre</b>, enjoy <b>outdoor activities year-round</b>, and experience the <b>unique atmosphere</b> of a place where history, adrenaline, and breathtaking scenery come together."
            )}
          </p>
          <p>
            {parseBold(
              "Before every competition the <b>flag carriers ski down the landing slope</b>. The most desired flag to carry is a Slovenian flag. The flag carriers should ski down the hill with empty stomachs in case of a fall and need of surgery. It is said that if a flag carrier falls, he will never get a girlfriend in the Upper Sava Valley."
            )}
          </p>
          <p>
            {parseBold(
              "The <b>song Planica, Planica</b> of the world-famous brothers Avsenik was written specially for the Ski Flying world championship in 1979. Since then, the chorus »Planica, Planica, snežena kraljica« (Eng: Planica, Planica, the snow queen) is played every time someone flies over 215 meters."
            )}
          </p>
          <p>
            {parseBold(
              "In 2025, the longest jump in a competition was made in Planica. <b>Domen Prevc flew 254.5 meters </b>, setting the 29th world record at this historic venue."
            )}
          </p>
          <img src={localAttractions} alt="local attractions" />
          <p>
            <span className="bold">{"Local attractions"}</span>
          </p>
          <p>
            {parseBold(
              "The area around Planica offers a perfect mix of <b>nature</b>, <b>sports</b>, and <b>alpine culture</b>. Nearby <b>Kranjska Gora</b> is a vibrant mountain resort with restaurants, shops, cycling routes, and winter sports. Just a short walk away, the stunning <b>Tamar Valley</b> invites visitors to enjoy peaceful hiking trails, waterfalls, and unforgettable views of the <b>Julian Alps</b>. For a true nature escape, explore <b>Triglav National Park</b>, known for its pristine landscapes, crystal-clear rivers, and rich alpine wildlife."
            )}
          </p>
        </div>
      </GetToKnowContainer>
      <LanguageGuideContainer>
        <h2>{"Slovenian for Beginners"}</h2>
        <p>{"Blend right in with these Slovenian phrases:"}</p>
        <p className="bold">{"Zdravo"}</p>
        <p>{"A common and versatile greeting, like »hello« or »hi«."}</p>
        <p className="bold">{"Dober dan"}</p>
        <p>{"A more formal greeting, like »good day«."}</p>
        <p className="bold">{"Nasvidenje"}</p>
        <p>{"A formal »goodbye« when you are leaving."}</p>
        <p className="bold">{"Adijo"}</p>
        <p>{"A less formal »goodbye«."}</p>
        <p className="bold">{"Kdor ne skače ni Sloven'c!"}</p>
        <p>
          {
            "You will hear this phrase a lot. Basically means »who do not jump is not a Slovene« and it is used by Slovenian fans."
          }
        </p>
      </LanguageGuideContainer>
      <FooterContainer>
        <p>{"Powered by"}</p>
        <img src={poweredBy} alt="powered by" />
        <p>{"Payment services by"}</p>
        <img src={paymeswissLogo} alt="payment services" />
        <br />
        <p>
          <a
            href="https://www.mastercard.com/global/en.html"
            onClick={() =>
              trackPlanicaButtonClick("open_privacy_policy", "footer")
            }
          >
            {"Privacy Policy"}
          </a>
        </p>
        <p>
          <a
            href="https://www.mastercard.com/global/en.html"
            onClick={() =>
              trackPlanicaButtonClick("open_terms_and_conditions", "footer")
            }
          >
            {"Terms & Conditions"}
          </a>
        </p>
        {serialNumber && (
          <div className="copyToClipboard">
            <p>ID: {serialNumber}</p>
            <img
              onClick={() => {
                trackPlanicaButtonClick("copy_serial_number", "footer");
                navigator.clipboard.writeText(serialNumber);
                setCopied(true);
              }}
              src={copyIconWhite}
              alt="copy to clipboard"
            />
          </div>
        )}
        <div className="version">
          <p>v.{packageJson.version}</p>
        </div>
        {copied && (
          <p>
            <b>Copied to clipboard</b>
          </p>
        )}
      </FooterContainer>
      {isMemoriesOpen && (
        <Memories
          serialNumber={serialNumber}
          onClose={() => handleClose("memories")}
          onTrackButtonClick={trackPlanicaButtonClick}
        />
      )}
      {isBalanceOpen && (
        <Balance
          onClose={() => handleClose("balance")}
          remainingBalance={balanceAndPin?.balanceDetails?.availableBalance}
          activationCode={balanceAndPin?.activationCode}
          onTrackButtonClick={trackPlanicaButtonClick}
        />
      )}
      {isProgramOpen && (
        <Program
          onClose={() => handleClose("program")}
          scheduleDays={deviceDetails?.schedules?.[0]?.days || []}
          scheduleError={errorGettingDeviceDetails}
        />
      )}

      {isYourStayOpen &&
        (() => {
          const hotelInfo = getHotelInfo();
          if (!hotelInfo) return null;
          return (
            <YourStay
              onClose={() => handleClose("yourStay")}
              hotelInfo={hotelInfo}
              hotelOptions={{
                selectedOptionId: hotelInfo.options?.[0]?.id
              }}
              walletURL={getWalletURL()}
              onTrackButtonClick={trackPlanicaButtonClick}
            />
          );
        })()}
      {weatherData && (
        <div style={{ position: "fixed", top: "90%", right: "10px" }}>
          <WeatherWidget
            weatherData={weatherData}
            loading={loadingWeather}
            error={errorWeather}
            onTrackButtonClick={trackPlanicaButtonClick}
          />
        </div>
      )}
    </MainContainer>
  );
};

export default Planica26;
